import React from 'react';
import './table-row-actions.scss';
import CustomIcon, {IconType} from '../custom-icon/custom-icon';
import useCustomModal from '../custom-modal/custom-modal';
import {CrudConfig, TableActionType} from '../../../model/table';
import usePromiseFeedback from '../promise-feedback/usePromiseFeedback';
import {Tooltip} from 'antd';

const RemoveModalBody = <T,>({ removeModal, rowData }: { removeModal: CrudConfig<T>['removeModal']; rowData: T }) => (
    <div className="modal-commit-message">
        {removeModal ? removeModal.body(rowData) : 'Remove this record?'}
    </div>
);

const renderRemoveModalBody = <T,>(removeModal: CrudConfig<T>['removeModal'], rowData: T) => () => (
    <RemoveModalBody removeModal={removeModal} rowData={rowData} />
);

interface TableRowActionsProps<T> {
    crudConfig: CrudConfig<T>;
    rowData: T;
    openToEdit?: () => void;
}

export const TableRowActions = <T, >(props: TableRowActionsProps<T>) => {
    const { crudConfig, rowData, openToEdit } = props;
    const [showModal, , contextHolder] = useCustomModal();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();

    const startEdit = () => {
        openToEdit?.();
    };

    const onRemove = () => {
        const removeModal = crudConfig.removeModal;
        showModal({
            wrapClassName: `remove-row-modal-wrapper remove-${crudConfig.entityName?.toLowerCase()}-modal`,
            width: 450,
            title: removeModal ? removeModal.title : 'Remove',
            body: renderRemoveModalBody(removeModal, rowData),
            okText: 'Delete',
            cancelText: 'Cancel',
            onOk: () => promiseFeedbackFactory({
                promise: Promise.resolve().then(() => crudConfig.removeCb!(rowData)),
                notifObj: { show: true, notificationMsg: removeModal?.successMsg ? removeModal.successMsg(rowData) : undefined }
            })
        });
    };

    return <div className={'actions-cell'}>
        <div className={'actions'}>
            {crudConfig.customActions?.(rowData)}
            {crudConfig.displayInlineActions && !!crudConfig.updateCb &&
                <Tooltip title={crudConfig.actionTooltips?.edit}
                         placement={crudConfig.actionTooltips?.placement}
                         overlayClassName={crudConfig.actionTooltips?.className}>
                <button type="button" className={'action-edit'} aria-label="Edit" onClick={startEdit}><CustomIcon type={IconType.PENCIL}/></button>
                </Tooltip>}
            {crudConfig.displayInlineActions && !!crudConfig.removeCb && !(crudConfig.disable?.[TableActionType.Remove]?.(rowData)) &&
                <Tooltip title={crudConfig.actionTooltips?.remove}
                         placement={crudConfig.actionTooltips?.placement}
                         overlayClassName={crudConfig.actionTooltips?.className}>
                <button type="button" className={'action-remove'} aria-label="Remove" onClick={onRemove}><CustomIcon type={IconType.TRASH}/></button>
                </Tooltip>}
        </div>
        {contextHolder}
        {promiseContextHolder}
    </div>;
};
